import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { useI18n } from "@/lib/i18n";
import { langPath } from "@/lib/routes";

const ORIGIN = "https://databridgar.com";

export default function PolicyPage({ docKey, metaKey, slug }) {
  const { t, tList, lang } = useI18n();
  const sections = tList(`${docKey}.sections`);

  return (
    <article className="relative">
      <Seo
        title={t(`meta.${metaKey}.title`)}
        description={t(`meta.${metaKey}.description`)}
        htmlLang={t("lang.htmlLang")}
        canonical={`${ORIGIN}${langPath(lang, slug)}`}
        alternates={[
          { hreflang: "es-AR", href: `${ORIGIN}${langPath("ar", slug)}` },
          { hreflang: "en", href: `${ORIGIN}${langPath("en", slug)}` },
        ]}
      />

      <div className="border-b border-hairline bg-paper2">
        <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <nav
            aria-label="breadcrumb"
            className="flex items-center gap-1.5 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-muted-ink/70"
          >
            <Link
              to={langPath(lang)}
              data-testid="policy-breadcrumb-home"
              className="link-underline hover:text-ink"
            >
              {t("nav.home")}
            </Link>
            <ChevronRight className="h-3 w-3" strokeWidth={2} />
            <span className="text-ink">{t("nav.legal")}</span>
          </nav>

          <h1
            data-testid="policy-title"
            className="mt-5 max-w-[24ch] font-display text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.02em] text-ink sm:text-[2.8rem]"
          >
            {t(`${docKey}.title`)}
          </h1>

          <div
            data-testid="policy-last-updated"
            className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted-ink"
          >
            <span>
              {t(`${docKey}.updatedLabel`)}: {t(`${docKey}.updated`)}
            </span>
            <span className="h-3 w-px bg-hairline" aria-hidden="true" />
            <span>
              {t(`${docKey}.versionLabel`)} {t(`${docKey}.version`)}
            </span>
          </div>

          <p className="mt-6 max-w-[68ch] text-[0.94rem] leading-7 text-muted-ink">
            {t(`${docKey}.intro`)}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <nav className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
              {t("common.sectionLabel")}
            </p>
            <ol className="mt-3 space-y-1.5 border-l border-hairline pl-4">
              {sections.map((s, i) => (
                <li key={i}>
                  <a
                    href={`#${docKey}-s${i}`}
                    data-testid={`policy-toc-${i}`}
                    className="link-underline block text-[0.8rem] leading-5 text-muted-ink hover:text-ink"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ol>
            <div className="mt-7 rounded-2xl border border-hairline bg-white p-5 shadow-card">
              <p className="text-[0.82rem] font-semibold text-ink">
                {t("footer.contactTitle")}
              </p>
              <a
                href={`mailto:${t("footer.emailPrivacy")}`}
                data-testid="policy-contact-email"
                className="mt-2 inline-flex items-center gap-2 text-[0.82rem] text-muted-ink hover:text-ink"
              >
                <Mail className="h-3.5 w-3.5 text-amber" strokeWidth={1.75} />
                <span className="link-underline">{t("footer.emailPrivacy")}</span>
              </a>
            </div>
          </nav>

          <div className="lg:col-span-8">
            {sections.map((s, i) => (
              <section
                key={i}
                id={`${docKey}-s${i}`}
                data-testid={`policy-section-${i}`}
                className="scroll-mt-28 border-t border-hairline2 py-8 first:border-0 first:pt-0"
              >
                <h2 className="max-w-[46ch] font-display text-[1.3rem] font-semibold leading-snug text-ink">
                  {s.heading}
                </h2>
                {Array.isArray(s.paragraphs) &&
                  s.paragraphs.map((p, j) => (
                    <p
                      key={j}
                      className="mt-4 max-w-[68ch] text-[0.9rem] leading-7 text-muted-ink"
                    >
                      {p}
                    </p>
                  ))}
                {Array.isArray(s.items) && s.items.length > 0 && (
                  <ul className="mt-5 max-w-[68ch] space-y-2.5">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex gap-3">
                        <span
                          aria-hidden="true"
                          className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber"
                        />
                        <span className="text-[0.9rem] leading-7 text-muted-ink">
                          {it}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.note && (
                  <p className="mt-5 max-w-[68ch] border-l-2 border-amber bg-paper2 px-4 py-3 text-[0.85rem] leading-6 text-ink">
                    {s.note}
                  </p>
                )}
              </section>
            ))}

            <div className="mt-10 flex flex-wrap gap-3 border-t border-hairline pt-8">
              <Link
                to={langPath(lang)}
                data-testid="policy-back-home"
                className="rounded-xl bg-ink px-5 py-2.5 text-[0.82rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2"
              >
                {t("common.backHome")}
              </Link>
              <a
                href={`mailto:${t("footer.emailGeneral")}`}
                data-testid="policy-write-us"
                className="rounded-xl border border-hairline px-5 py-2.5 text-[0.82rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2"
              >
                {t("common.writeUs")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
